/** The product's button, in the two weights the design review settled (KD, 2026-11-01).
 *
 * - **primary** is solid teal and is for the page-level action only (Add, Issue). One per page.
 * - **secondary** (the default) is white with teal text and a border: every row action, every Import,
 *   every other button.
 *
 * **A disabled button is neutral grey, never a faded accent**, and it says why: `reason` is printed
 * beneath the button (small, visible, and the button's `aria-describedby`), not left in a tooltip
 * that a keyboard or a touch screen never sees. A control that is absent is better than one that is
 * disabled for a role that can never use it (`AddImportActions` draws nothing for that role); this is
 * for the person who *can* act and cannot yet - "Confirm the vendor to continue".
 */

import type { ButtonHTMLAttributes, ReactNode } from "react";
import { useId } from "react";

import styles from "./ActionButton.module.css";

export interface ActionButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  variant?: "primary" | "secondary";
  /** Why the button is disabled. Giving one disables the button. */
  reason?: string;
  children: ReactNode;
}

export function ActionButton({
  variant = "secondary",
  reason,
  disabled,
  type = "button",
  children,
  ...rest
}: ActionButtonProps) {
  const reasonId = useId();
  const off = disabled === true || reason !== undefined;
  return (
    <span className={styles.wrap}>
      <button
        {...rest}
        type={type}
        className={variant === "primary" ? styles.primary : styles.secondary}
        disabled={off}
        aria-describedby={reason !== undefined ? reasonId : rest["aria-describedby"]}
      >
        {children}
      </button>
      {reason !== undefined ? (
        <span id={reasonId} className={styles.reason} data-testid="action-reason">
          {reason}
        </span>
      ) : null}
    </span>
  );
}
